import React from "react";
import { Reorder, useDragControls } from "framer-motion";
import { GripVertical, ChevronUp, ChevronDown } from "lucide-react";

// Keep keys in sync with REPORT_MODULES in backend/pdf_report.py.
export const REPORT_MODULES = [
  ["summary", "Executive summary & wins", "Cover summary, traffic value, movers and AI wins"],
  ["legend", "How to read this report", "Legend explaining arrows and colours"],
  ["kpis", "Key metrics", "Ranking keywords, traffic, referring domains, rank / DA"],
  ["work", "Work completed", "This month's account work notes"],
  ["ga4", "Google Analytics", "Sessions, users and conversions (last 28 days)"],
  ["visibility", "Search visibility chart", "Visibility trend across rank checks"],
  ["rankings", "Rankings table", "Latest positions for tracked keywords"],
  ["local", "Local SEO / Map Pack", "Map Pack positions, Google Business card, local competitors"],
  ["backlinks", "Backlink profile", "Backlinks, referring domains and rank"],
  ["pagespeed", "Core Web Vitals", "PageSpeed scores for top pages"],
  ["competitors", "Competitors & share of voice", "Head-to-head cards and share of voice"],
];

const META = Object.fromEntries(REPORT_MODULES.map(([k, l, d]) => [k, { label: l, desc: d }]));

// Saved list + any modules missing from it (enabled, appended) — mirrors the backend.
export function normalizeReportModules(saved) {
  const seen = new Set();
  const out = [];
  for (const m of saved || []) {
    if (m && META[m.key] && !seen.has(m.key)) {
      out.push({ key: m.key, enabled: m.enabled !== false });
      seen.add(m.key);
    }
  }
  for (const [k] of REPORT_MODULES) if (!seen.has(k)) out.push({ key: k, enabled: true });
  return out;
}

function ModuleRow({ m, index, total, onToggle, onMove }) {
  const controls = useDragControls();
  const meta = META[m.key];
  return (
    <Reorder.Item
      value={m.key}
      dragListener={false}
      dragControls={controls}
      data-testid={`report-module-${m.key}`}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border bg-white transition-colors ${
        m.enabled ? "border-[#e2e8f0]" : "border-dashed border-[#e2e8f0] opacity-60"
      }`}
    >
      <button
        type="button"
        onPointerDown={(e) => controls.start(e)}
        className="cursor-grab active:cursor-grabbing text-[#a0aec0] hover:text-[#4a5568] touch-none"
        title="Drag to reorder"
        data-testid={`report-module-${m.key}-drag`}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="font-mono-tight text-[10px] text-[#a0aec0] w-4">{index + 1}</span>
      <label className="flex-1 flex items-start gap-2.5 cursor-pointer select-none min-w-0">
        <input
          type="checkbox"
          checked={m.enabled}
          onChange={() => onToggle(m.key)}
          data-testid={`report-module-${m.key}-toggle`}
          className="mt-0.5 h-4 w-4 rounded border-[#cbd5e0] text-[#38b2ac] focus:ring-[#4fd1c5]"
        />
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold text-[#1a202c]">{meta.label}</span>
          <span className="block text-[11px] text-[#718096] truncate">{meta.desc}</span>
        </span>
      </label>
      <div className="flex flex-col">
        <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)} className="text-[#a0aec0] hover:text-[#1a202c] disabled:opacity-30" data-testid={`report-module-${m.key}-up`} title="Move up">
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button type="button" disabled={index === total - 1} onClick={() => onMove(index, 1)} className="text-[#a0aec0] hover:text-[#1a202c] disabled:opacity-30" data-testid={`report-module-${m.key}-down`} title="Move down">
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>
    </Reorder.Item>
  );
}

export default function ReportModulesEditor({ value, onChange }) {
  const modules = normalizeReportModules(value);
  const toggle = (key) => onChange(modules.map((m) => (m.key === key ? { ...m, enabled: !m.enabled } : m)));
  const move = (i, dir) => {
    const next = [...modules];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    onChange(next);
  };
  const onCount = modules.filter((m) => m.enabled).length;
  return (
    <div data-testid="report-modules-editor">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] text-[#718096]" data-testid="report-modules-count">
          {onCount} of {modules.length} modules included · cover page and client snapshot always appear
        </span>
        <button
          type="button"
          onClick={() => onChange(REPORT_MODULES.map(([key]) => ({ key, enabled: true })))}
          className="text-[11px] font-semibold text-[#319795] hover:underline"
          data-testid="report-modules-reset"
        >
          Reset to default
        </button>
      </div>
      <Reorder.Group
        axis="y"
        values={modules.map((m) => m.key)}
        onReorder={(keys) => onChange(keys.map((k) => modules.find((m) => m.key === k)))}
        className="space-y-1.5"
      >
        {modules.map((m, i) => (
          <ModuleRow key={m.key} m={m} index={i} total={modules.length} onToggle={toggle} onMove={move} />
        ))}
      </Reorder.Group>
    </div>
  );
}
